import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { dataReference, formatGrainTick, windowGrainSeries, type RangeValue } from "@/lib/time-range";

import {
  ProfitYieldError,
  getCostStack,
  messageFor,
  type CostStackBucket,
  type CostStackComponent,
  type CostStackGrain,
  type Region,
} from "../api";
import type { Controls } from "../controls";
import { formatCount, formatGbp, formatPct, isProvisionalMonth } from "../format";

/**
 * What each cost component is called on screen. The store's keys are not
 * sentences a marketer says - `cogs_and_fulfilment_unsplit` is a real state of
 * the data (the two costs arrive as one figure for some markets) and the label
 * has to say that rather than hide it behind a tidier word.
 */
const COMPONENT_LABELS: Record<CostStackComponent, string> = {
  ads_spend: "Ads spend",
  cogs: "Cost of goods",
  fulfilment: "Fulfilment",
  cogs_and_fulfilment_unsplit: "Cost of goods (unsplit)",
  refunds: "Refunds",
  discounts: "Discounts",
  net_contribution_profit: "Net contribution profit",
};

/**
 * The stack's fills. `refunds` gets the warn token (money leaving through
 * returns is a caution, not neutral) and `net_contribution_profit` gets the
 * pass token (it is the part a reader is looking for) - everything else
 * stays inside the brand purple family, since they are parts of one total,
 * not seven separate judgements.
 */
const COMPONENT_FILLS: Record<CostStackComponent, string> = {
  ads_spend: "var(--color-ks-purple-shade)",
  cogs: "var(--color-ks-purple)",
  fulfilment: "var(--color-ks-purple-3)",
  cogs_and_fulfilment_unsplit: "var(--color-ks-purple-2)",
  refunds: "var(--color-ks-warn)",
  discounts: "var(--color-ks-purple-4)",
  net_contribution_profit: "var(--color-ks-pass)",
};

/**
 * The `net_contribution_profit` bar's per-bucket fill (W4/H6). Pure and
 * exported so the sign rule is tested directly rather than through a
 * recharts SSR render, which does not draw children without a measured
 * container.
 */
export function netContributionFill(value: number | string | null): string {
  return typeof value === "number" && value < 0 ? "var(--color-ks-fail)" : "var(--color-ks-pass)";
}

/** The tooltip row label for a cost-stack component (W4): "Loss" replaces the
 * usual name only for a negative `net_contribution_profit` bucket. */
export function tooltipLabelFor(component: CostStackComponent, value: number | string | null): string {
  const negative = component === "net_contribution_profit" && typeof value === "number" && value < 0;
  return negative ? "Loss" : COMPONENT_LABELS[component];
}

/**
 * The present buckets inside the page Range (KET-6591). The Range and the
 * Period bound the stack like every other chart on the page (bug
 * 3f30402f-c6c9-81e8). Pure and exported so the pass-through is tested directly.
 */
export function windowCostBuckets(
  buckets: CostStackBucket[],
  grain: CostStackGrain,
  range: RangeValue,
  reference: Date,
): CostStackBucket[] {
  return windowGrainSeries(
    buckets.filter((bucket) => bucket.present).map((bucket) => ({ ...bucket, m: bucket.bucket })),
    grain,
    range,
    reference,
  );
}

const COST_STACK_CONTROL_SPECS: FilterSpec[] = [
  {
    name: "cost_basis",
    label: "Mode",
    kind: "choice",
    options: [
      { value: "per_unit", label: "GBP per unit" },
      { value: "pct_of_sales", label: "% of sales" },
    ],
  },
];

export function CostStackPanel({
  controls,
  region,
  areaName,
}: {
  controls: Controls;
  region?: Region;
  areaName: string;
}) {
  // The page Period IS the bar width (bug 3f30402f-c6c9-81e8).
  const { grain, costMode: mode } = controls;

  const query = useQuery({
    queryKey: ["meteoric_profit_yield", "cost-stack", grain, region ?? "all"],
    queryFn: () => getCostStack(grain, region),
  });

  const controlValues = { ...controls.values, cost_basis: mode };

  if (query.isPending) return <Skeleton className="h-72 w-full" />;

  if (query.isError) {
    // A grain the producer has not written yet is a missing series, not an
    // outage - say so the way the trend's empty state does.
    const notYet = query.error instanceof ProfitYieldError && query.error.status === 404;
    return (
      <Alert>
        <AlertTitle>Cost stack unavailable</AlertTitle>
        <AlertDescription>
          {notYet
            ? `No ${grain} cost stack has been computed yet. Try another Period.`
            : messageFor(query.error, areaName)}
        </AlertDescription>
      </Alert>
    );
  }

  const stack = query.data.data;
  const reference = dataReference(query.data.meta.data_as_of, stack.generated_at);
  const buckets = windowCostBuckets(stack.modes[mode].buckets, grain, controls.range, reference);
  const components = stack.components;

  const data = buckets.map((bucket) => {
    const row: Record<string, string | number | null> = {
      bucket: bucket.bucket,
      attributed_units: bucket.attributed_units,
      attributed_sales_devat_gbp: bucket.attributed_sales_devat_gbp,
      provisional: grain === "monthly" && isProvisionalMonth({ m: bucket.bucket.slice(0, 7), provisional: bucket.provisional }, reference) ? 1 : 0,
    };
    for (const component of components) row[component] = bucket[component];
    return row;
  });

  const format = (value: number | null) =>
    mode === "pct_of_sales" ? formatPct(value) : `GBP ${formatGbp(value)}`;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ks-ink">CAC cost stack</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              What consumed each unit of ads-attributed sales.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <FilterBar
              layout="segmented"
              filters={COST_STACK_CONTROL_SPECS}
              values={controlValues}
              onChange={controls.onChange}
            />
            <p className="text-[11px] text-muted-foreground">Follows the page Range and Period.</p>
          </div>
        </div>

        {/* The identity check is the honest headline of this panel. A stack whose
            parts do not add back to sales is still worth showing - it is often the
            only clue that a rate table is missing - but it must never be drawn as
            though it reconciles. */}
        {!stack.identity_ok && (
          <Alert>
            <AlertTitle>Stack does not reconcile.</AlertTitle>
            <AlertDescription>
              The component total differs from ads-attributed sales for one or more buckets.
            </AlertDescription>
          </Alert>
        )}

        <ChartFrame
          title="CAC cost stack"
          pointCount={data.length}
          height={320}
          emptyMessage="No cost-stack buckets have been computed for this period yet."
        >
          <BarChart data={data} stackOffset="sign" margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
            <XAxis
              dataKey="bucket"
              tick={{ fontSize: 12 }}
              stroke="var(--color-ks-grey-1)"
              tickFormatter={(value: string) => formatGrainTick(value, grain)}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="var(--color-ks-grey-1)"
              width={56}
              tickFormatter={(value: number) =>
                mode === "pct_of_sales" ? `${value.toFixed(0)}%` : value.toFixed(2)
              }
            />
            <Tooltip
              content={
                <CostStackTooltip
                  components={components}
                  format={format}
                  noRatePct={stack.coverage.no_rate_sales_pct}
                />
              }
            />
            <ReferenceLine y={0} stroke="var(--color-ks-ink)" />
            {components.map((component) =>
              component === "net_contribution_profit" ? (
                // W4: a loss quarter of the stack must read as a loss, not as
                // the same profit-purple as a gain - the one series whose
                // sign changes the reader's verdict gets a per-bucket fill.
                <Bar key={component} dataKey={component} stackId="cost" isAnimationActive={false}>
                  {data.map((row, index) => (
                    <Cell key={index} fill={netContributionFill(row[component])} />
                  ))}
                </Bar>
              ) : (
                <Bar
                  key={component}
                  dataKey={component}
                  stackId="cost"
                  fill={COMPONENT_FILLS[component] ?? "var(--color-ks-grey-2)"}
                  isAnimationActive={false}
                />
              ),
            )}
          </BarChart>
        </ChartFrame>

        <Card>
          <CardContent className="flex flex-col gap-2 text-xs text-muted-foreground">
            {/* W11: exactly the three facts a reader needs to trust the stack -
                "Sales with no rate" moved into the per-bar tooltip, since it is
                a coverage caveat about the numbers under the cursor, not a
                headline fact on its own. */}
            <dl className="grid gap-2 md:grid-cols-3">
              <div>
                <dt className="font-medium text-ks-ink">Margin split coverage</dt>
                <dd>{formatPct(stack.coverage.margin_split_coverage_pct)}</dd>
              </div>
              <div>
                <dt className="font-medium text-ks-ink">Leakage coverage</dt>
                <dd>{formatPct(stack.coverage.leakage_coverage_pct)}</dd>
              </div>
              <div>
                <dt className="font-medium text-ks-ink">Rates as of</dt>
                <dd>{stack.coverage.rates_as_of ?? "not stated"}</dd>
              </div>
            </dl>
            <p>{stack.basis_note}</p>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}

type TooltipPayloadEntry = { payload?: Record<string, string | number | null> };

/** The cost-stack tooltip heading; a provisional month says so. Exported for tests. */
export function costTooltipTitle(row: { bucket?: unknown; provisional?: unknown }): string {
  return `${String(row.bucket ?? "")}${row.provisional === 1 ? " (provisional)" : ""}`;
}

function CostStackTooltip({
  active,
  payload,
  components,
  format,
  noRatePct,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  components: CostStackComponent[];
  format: (value: number | null) => string;
  noRatePct: number | null;
}): ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const units = typeof row.attributed_units === "number" ? row.attributed_units : null;
  const sales = typeof row.attributed_sales_devat_gbp === "number" ? row.attributed_sales_devat_gbp : null;

  return (
    <div className="rounded-ks-sm bg-ks-ink px-2 py-1.5 text-xs text-ks-surface">
      <p className="font-semibold">{costTooltipTitle(row)}</p>
      <p>{units === null ? "n/a attributed units" : `${formatCount(units)} attributed units`}</p>
      <p>Sales: GBP {sales === null ? "n/a" : sales.toFixed(2)}</p>
      {components.map((component) => {
        const value = row[component];
        const label = tooltipLabelFor(component, value);
        return (
          <p key={component}>
            {label}: {typeof value === "number" ? format(value) : "n/a"}
          </p>
        );
      })}
      {/* W11: this coverage caveat now lives here, beside the numbers it
          qualifies, instead of as a standalone headline fact. */}
      <p>Sales with no rate: {formatPct(noRatePct)}</p>
    </div>
  );
}
